import { Download, ExternalLink, Info } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { buildActivityOperationPath } from "@/modules/dashboard/payments-activity/utils/activityLinks";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAmountDisplay } from "@/shared/components/data-display/ColocaAmountDisplay";
import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import useTranslations from "@/shared/lang/useTranslations";
import { formatDateTime } from "@/shared/utils/dateTimeHelpers";

import type {
  LedgerOperationCandidate,
  LedgerOperationKind,
  LedgerOperationView,
} from "../types/paymentsLedger.types";
import { LedgerCopyableId } from "./LedgerCopyableId";
import { LedgerStatusPill } from "./LedgerStatusPill";

export interface LedgerOperationHeaderProps {
  kind: LedgerOperationKind;
  operationId: string;
  /** The live operation, when the runtime could be read. Null → the header renders from the journal alone. */
  candidate: LedgerOperationCandidate | null;
  organizationName: string | null;
  view: LedgerOperationView | null;
  /**
   * True when the entity read failed — disclosed, because the status shown would otherwise be missing
   * silently.
   */
  isStatusUnavailable: boolean;
  onExportCsv: () => void;
  canExport: boolean;
}

function MetaItem({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
      <div className="mt-0.5 text-sm text-foreground">{children}</div>
    </div>
  );
}

/**
 * Identity card of the operation whose journal is on screen.
 *
 * It is built from two independent sources and works with either one: the journal itself always provides the
 * organization (through the client-liability account scope), the currencies and the date of the first entry,
 * while the runtime entity adds the live status and the headline amount. When the entity cannot be read — a
 * swap reached by deep link before its organization is known, a deleted id — that is disclosed instead of
 * quietly dropping the status.
 */
export function LedgerOperationHeader({
  kind,
  operationId,
  candidate,
  organizationName,
  view,
  isStatusUnavailable,
  onExportCsv,
  canExport,
}: LedgerOperationHeaderProps) {
  const { t, activeLanguage } = useTranslations("paymentsLedger");
  const navigate = useNavigate();

  /**
   * Round trip to the operation itself. Only payouts and payins: the activity view reads those two endpoints,
   * and a swap has no row there — offering the link for one would land on a list that cannot contain it.
   */
  const activityPath = kind === "swaps" ? null : buildActivityOperationPath(kind, operationId);

  const kindLabel =
    kind === "payouts"
      ? t("header_kind_payout")
      : kind === "payins"
        ? t("header_kind_payin")
        : t("header_kind_swap");

  return (
    <div className="space-y-3">
      <div className="rounded-xl border border-border bg-muted/40 p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <ColocaBadge variant="primary" className="font-sans">
                {kindLabel}
              </ColocaBadge>
              {candidate && <LedgerStatusPill candidate={candidate} />}
            </div>
            {candidate && (
              <ColocaAmountDisplay
                value={candidate.amount}
                currency={candidate.currency}
                size="lg"
                locale={activeLanguage}
                minimumFractionDigits={2}
                maximumFractionDigits={8}
              />
            )}
            {candidate && <p className="text-xs text-muted-foreground">{candidate.subtitle}</p>}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {activityPath && (
              <ColocaButton
                variant="default"
                size="sm"
                icon={ExternalLink}
                onClick={() => navigate(activityPath)}
              >
                {t("header_view_operation")}
              </ColocaButton>
            )}
            <ColocaButton
              variant="default"
              size="sm"
              icon={Download}
              disabled={!canExport}
              onClick={onExportCsv}
            >
              {t("header_export_csv")}
            </ColocaButton>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 gap-4 border-t border-border pt-4 sm:grid-cols-2 xl:grid-cols-4">
          <MetaItem label={t("header_operation_id")}>
            <LedgerCopyableId value={operationId} truncate />
          </MetaItem>
          <MetaItem label={t("header_organization")}>
            <span className="truncate">{organizationName ?? t("header_organization_unknown")}</span>
          </MetaItem>
          <MetaItem label={t("header_created")}>
            {view?.firstPostedAt
              ? formatDateTime(view.firstPostedAt, { locale: activeLanguage, includeTime: true })
              : candidate
                ? formatDateTime(candidate.createdAt, { locale: activeLanguage, includeTime: true })
                : "—"}
          </MetaItem>
          <MetaItem label={t("header_currencies")}>
            <span className="font-mono text-sm">{view?.currencies.join(" · ") || "—"}</span>
          </MetaItem>
        </div>
      </div>

      {isStatusUnavailable && (
        <ColocaAlert variant="secondary" icon={Info} text={t("header_status_unavailable")} />
      )}
    </div>
  );
}
